import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Check, Mail, Zap, Cpu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
  hidden: {},
};

function Section({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function FadeUp({ children, index = 0, className = '' }: { children: React.ReactNode; index?: number; className?: string }) {
  return (
    <motion.div variants={fadeUp} custom={index} className={className}>
      {children}
    </motion.div>
  );
}

export default function Pricing() {
  const { t } = useTranslation('pricing');

  const plans = [
    {
      name: t('plans.free.name'),
      price: t('plans.free.price'),
      period: t('plans.free.period'),
      desc: t('plans.free.desc'),
      quota: t('plans.free.quota'),
      features: t('plans.free.features', { returnObjects: true }) as string[],
      cta: t('plans.free.cta'),
      href: '/register',
      highlighted: false,
    },
    {
      name: t('plans.monthly.name'),
      price: t('plans.monthly.price'),
      period: t('plans.monthly.period'),
      desc: t('plans.monthly.desc'),
      quota: t('plans.monthly.quota'),
      features: t('plans.monthly.features', { returnObjects: true }) as string[],
      cta: t('plans.monthly.cta'),
      href: 'mailto:aivoice@qq.com?subject=VIP月付开通申请',
      highlighted: false,
    },
    {
      name: t('plans.lifetime.name'),
      price: t('plans.lifetime.price'),
      period: t('plans.lifetime.period'),
      desc: t('plans.lifetime.desc'),
      quota: t('plans.lifetime.quota'),
      features: t('plans.lifetime.features', { returnObjects: true }) as string[],
      cta: t('plans.lifetime.cta'),
      href: 'mailto:aivoice@qq.com?subject=永久会员开通申请',
      highlighted: true,
      badge: t('plans.lifetime.badge'),
    },
    {
      name: t('plans.enterprise.name'),
      price: t('plans.enterprise.price'),
      period: t('plans.enterprise.period'),
      desc: t('plans.enterprise.desc'),
      quota: t('plans.enterprise.quota'),
      features: t('plans.enterprise.features', { returnObjects: true }) as string[],
      cta: t('plans.enterprise.cta'),
      href: 'mailto:aivoice@qq.com?subject=企业版咨询',
      highlighted: false,
    },
  ];

  const faqs = t('faq.items', { returnObjects: true }) as { q: string; a: string }[];

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <div className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('hero.subtitle')}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight">
              {t('hero.title')}
            </h1>
            <p className="mt-4 text-gray-500 text-lg max-w-2xl mx-auto">
              {t('hero.description')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Plans */}
      <Section className="pb-28 sm:pb-36 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, i) => (
              <FadeUp key={plan.name} index={i}>
                <div className={`relative flex flex-col p-8 rounded-2xl border transition-all duration-500 h-full ${
                  plan.highlighted
                    ? 'border-blue-200 bg-blue-50/50 shadow-lg shadow-blue-500/5 ring-1 ring-blue-100'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }`}>
                  {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 rounded-full text-xs font-semibold text-white">
                    {plan.badge || t('recommended')}
                  </div>
                )}

                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{plan.desc}</p>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    {plan.period && <span className="text-gray-400 ml-1">{plan.period}</span>}
                  </div>

                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 w-fit mb-6">
                    <Zap className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">{plan.quota}</span>
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.name === t('plans.free.name') ? (
                    <a
                      href={plan.href}
                      className="flex items-center justify-center gap-2 w-full px-6 py-3.5 border border-gray-200 hover:border-gray-300 text-gray-700 font-semibold rounded-xl transition-all duration-300 hover:bg-gray-50"
                    >
                      {plan.cta}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  ) : (
                    <a
                      href={plan.href}
                      className={`flex items-center justify-center gap-2 w-full px-6 py-3.5 font-semibold rounded-xl transition-all duration-300 ${
                        plan.highlighted
                          ? 'bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg hover:shadow-blue-500/20'
                          : 'border border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      {plan.cta}
                    </a>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp index={3}>
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-gray-50 border border-gray-200">
                <Cpu className="w-5 h-5 text-blue-600" />
                <span className="text-gray-500 text-sm">
                  {t('gpuHint.text')}
                  <a href="mailto:aivoice@qq.com" className="text-blue-600 hover:text-blue-500 font-medium ml-1 transition-colors">
                    {t('gpuHint.link')}
                  </a>
                </span>
              </div>
            </div>
          </FadeUp>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-28 sm:py-36 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-20">
          <FadeUp>
            <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('faq.subtitle')}</p>
          </FadeUp>
          <FadeUp index={1}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {t('faq.title')}
            </h2>
          </FadeUp>

          <div className="mt-12 space-y-6">
            {faqs.map((faq, i) => (
              <FadeUp key={i} index={i + 2}>
                <div className="p-6 rounded-xl bg-white border border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">{faq.q}</h3>
                  <p className="mt-2 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          <div className="flex items-center gap-6">
            <a href="/api-docs" className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
              {t('footer.apiDocs')}
            </a>
            <a href="https://github.com/shuzibabay-aigc/aivoice-skill" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
              {t('footer.github')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
